import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { makeRemoteSendPasswordResetEmail } from 'main/factories/usecases/send-password-reset-email';
import { showToast, TEN_SECONDS, TOAST_ERROR, TOAST_SUCCESS } from 'lib/utils';
import { SendPasswordResetEmail } from 'domain/usecases/send-password-reset-email';

/**
 * Custom hook for sending password reset emails
 * @param {UseMutationOptions} options - Optional configuration options
 * @returns {Object} Mutation object with various properties and methods
 */
export const useSendPasswordResetEmail = (
  options?: UseMutationOptions<SendPasswordResetEmail.Model, Error, SendPasswordResetEmail.Params>
) => {
  const sendPasswordResetEmailService = makeRemoteSendPasswordResetEmail();

  const sendPasswordResetEmailFn = async (params: SendPasswordResetEmail.Params) => {
    return sendPasswordResetEmailService.sendPasswordResetEmail(params);
  };

  const { onSuccess: customOnSuccess, onError: customOnError, ...rest } = options || {};

  return useMutation({
    mutationFn: sendPasswordResetEmailFn,

    onError: (error, variables, context, mutationContext) => {
      showToast(`Error sending password email: ${error.message}`, TOAST_ERROR, TEN_SECONDS);
      customOnError?.(error, variables, context, mutationContext);
    },

    onSuccess: (data, variables, context, mutationContext) => {
      showToast('Successfully sent email', TOAST_SUCCESS, TEN_SECONDS);
      customOnSuccess?.(data, variables, context, mutationContext);
    },

    ...rest,
  });
};
